import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { approvalAPI, ApprovalRequest } from '../services/ApprovalAPI';
import localAuthService from '../services/LocalAuthService';

interface RoleRequest {
  RequestId: string;
  RequestType: string;
  UserDisplayName: string;
  SapUsername: string;
  RoleName: string;
  OldRoleName?: string;
  RequestedBy: string;
  RequestedDate: string;
  Justification: string;
  ManagerName: string;
  ManagerEmail: string;
}

const ApprovalPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<RoleRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (requestId) {
      fetchRequestDetails();
    }
  }, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      if (requestId) {
        const requestData = await approvalAPI.getRequestDetails(requestId);
        if (requestData) {
          setRequest(requestData);
        } else {
          setError('Request not found');
        }
      }
    } catch (err) {
      setError('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!request) return;

    try {
      setProcessing(true);
      setError(null);

      // Get authenticated user's email, fallback to manager email if not authenticated
      const userEmail = localAuthService.getUserEmail();
      const approverEmail = userEmail || request.ManagerEmail;

      const approvalRequest: ApprovalRequest = {
        requestId: request.RequestId,
        action: 'approve',
        approverEmail: approverEmail,
        notes: notes,
        BNAME: request.SapUsername,
        ROLE_NAME: request.RoleName
      };

      const result = await approvalAPI.processApproval(approvalRequest);

      if (result.success) {
        navigate('/approval-success', { 
          state: { 
            action: 'approved', 
            requestId: request.RequestId,
            userDisplayName: request.UserDisplayName 
          } 
        });
      } else {
        setError(result.message || 'Failed to approve request');
      }
    } catch (err) {
      setError('An error occurred while processing the approval');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = () => {
    navigate(`/request/reject/${requestId}`, { 
      state: { request } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Request Not Found</h1>
          <p className="text-gray-600 mb-4">The approval request could not be found.</p>
          <button 
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Role Change Approval Required</h1>
            <p className="text-gray-600 mt-2">Review the request details below and take action</p>
          </div>
        </div>

        {/* Request Details */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Request Details</h2>
          </div>
          <div className="px-6 py-4">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Request Type</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    request.RequestType === 'AddRole' ? 'bg-green-100 text-green-800' :
                    request.RequestType === 'RemoveRole' ? 'bg-red-100 text-red-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {request.RequestType}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Request ID</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">{request.RequestId}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">User</dt>
                <dd className="mt-1 text-sm text-gray-900">{request.UserDisplayName}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">SAP Username</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">{request.SapUsername}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Role</dt>
                <dd className="mt-1 text-sm text-gray-900">{request.RoleName}</dd>
              </div>
              {request.OldRoleName && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">From Role</dt>
                  <dd className="mt-1 text-sm text-gray-900">{request.OldRoleName}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">Requested By</dt>
                <dd className="mt-1 text-sm text-gray-900">{request.RequestedBy}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Request Date</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(request.RequestedDate).toLocaleString()}
                </dd>
              </div>
            </dl>
            
            <div className="mt-6">
              <dt className="text-sm font-medium text-gray-500">Justification</dt>
              <dd className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                {request.Justification}
              </dd>
            </div>
          </div>
        </div>

        {/* Approval Notes */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Approval Notes (Optional)</h2>
          </div>
          <div className="px-6 py-4">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add any notes or comments about this approval..."
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleApprove}
                disabled={processing}
                className="flex-1 bg-green-600 text-white px-6 py-3 rounded-md font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {processing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Approve Request
                  </>
                )}
              </button>
              
              <button
                onClick={handleReject}
                disabled={processing}
                className="flex-1 bg-red-600 text-white px-6 py-3 rounded-md font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Reject Request
              </button>
            </div>
            
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                ← Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalPage;
